import { Euler } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  CONNECTOR_BOX_DEPTH,
  CONNECTOR_BOX_HEIGHT,
  CONNECTOR_BOX_OFFSET,
  CONNECTOR_BOX_WIDTH_ALONG_WALL,
  CONNECTOR_GAP_WIDTH,
} from './constants'

export type ConnectorKind = 'tensile' | 'vertical'

/**
 * Vertical box (world Z axis) with a rectangular footprint in the XY plane:
 * 1. Unit box scaled to wall-aligned width × depth × height.
 * 2. Rotate X by π/2 so the box height is vertical (same as SimpleCylinderMesh).
 * 3. Rotate Y by wall direction + 90° so the long edge follows the wall.
 */
export const getConnectorBoxRotation = (domain: Domain): Euler => {
  const tangent = domain.end.sub(domain.start)
  const tangentXY = new ImmutableVector3(tangent.x, tangent.y, 0)
  const tangentXYRotation = Math.atan2(tangentXY.y, tangentXY.x)

  const yRotation = tangentXYRotation

  return new Euler(Math.PI / 2, yRotation, 0)
}

export const getConnectorHalfBoxHeight = (fullHeight = CONNECTOR_BOX_HEIGHT): number =>
  fullHeight / 2

/**
 * Places a half-height box in the upper or lower slot so tensile and vertical
 * connectors at the same anchor do not overlap.
 *
 * Supports (add upward): vertical → upper, tensile → lower.
 * Targets (subtract downward): tensile → upper (less far down), vertical → lower.
 *
 * Vertical always uses the larger offset; tensile the smaller. Direction flips with isTarget.
 */
export const getConnectorBoxPositionWithOffset = (
  position: ImmutableVector3,
  isTarget: boolean,
  kind: ConnectorKind,
  fullHeight = CONNECTOR_BOX_HEIGHT,
  offset = CONNECTOR_BOX_OFFSET,
): ImmutableVector3 => {
  const halfHeight = getConnectorHalfBoxHeight(fullHeight)
  const useLargerOffset = kind === 'vertical'

  const centerOffsetFromAnchor = useLargerOffset
    ? offset + halfHeight / 2
    : offset + halfHeight / 2 + halfHeight + CONNECTOR_GAP_WIDTH

  return isTarget
    ? position.sub(new ImmutableVector3(0, 0, centerOffsetFromAnchor))
    : position.add(new ImmutableVector3(0, 0, centerOffsetFromAnchor))
}

/** Scale for a unit 1×1×1 box: [along wall, height, across wall]. */
export const getConnectorBoxScale = (
  height = CONNECTOR_BOX_HEIGHT,
  kind: ConnectorKind,
): [number, number, number] => {
  return [
    kind === 'vertical' ? CONNECTOR_BOX_WIDTH_ALONG_WALL * 1.5 : CONNECTOR_BOX_WIDTH_ALONG_WALL,
    height,
    CONNECTOR_BOX_DEPTH,
  ]
}

export const UNIT_BOX_GEOMETRY_ARGS: [number, number, number] = [1, 1, 1]
